import { forwardRef, useRef, type TextareaHTMLAttributes, type UIEvent } from "react";
import type { TemplateVariable } from "@shared/modules/x-messagerie";
import { Textarea } from "../../../core/components/ui/input";
import { insertAt, splitVariables } from "../lib/variables";

type Editable = HTMLInputElement | HTMLTextAreaElement;

/**
 * Insère `{{name}}` là où se trouve le curseur du champ (ou à la fin), replace le curseur
 * juste après et renvoie le nouveau texte. Demande de l'atelier du 26 octobre 2026 :
 * cliquer une variable plutôt que la retaper.
 */
export function insertVariable(el: Editable | null, value: string, name: string): string {
  const token = `{{${name}}}`;
  const { value: next, caret } = insertAt(
    value,
    token,
    el?.selectionStart ?? value.length,
    el?.selectionEnd ?? value.length,
  );
  if (el) {
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  }
  return next;
}

/** Liste des variables d'un modèle ; chaque nom est un bouton qui l'insère dans le texte. */
export function VariableChips({
  variables,
  onInsert,
  open = false,
}: {
  variables: TemplateVariable[];
  onInsert: (name: string) => void;
  open?: boolean;
}) {
  return (
    <details className="modele__help" open={open}>
      <summary className="modele__help-summary">Variables disponibles</summary>
      <p className="modele__help-hint">
        Cliquez une variable pour l’insérer à la position du curseur, dans le message ou l’objet.
      </p>
      <dl className="modele__vars">
        {variables.map((v) => (
          <div key={v.name} className="modele__var">
            <dt>
              <button
                type="button"
                className="modele__var-button"
                title="Insérer dans le texte"
                // Garder le focus (et le curseur) dans le champ pendant le clic.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onInsert(v.name)}
              >
                <code>{`{{${v.name}}}`}</code>
              </button>
            </dt>
            <dd>{v.description}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

/**
 * Zone de texte dont les variables sont surlignées : un calque derrière le champ porte
 * le fond et les marques, le champ transparent garde le texte, le curseur et la
 * sélection. Même boîte (classe `.textarea`) des deux côtés pour que les marques tombent
 * sous les bons mots ; le défilement est synchronisé.
 */
export const HighlightedTextarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { value: string }
>(function HighlightedTextarea({ value, className, onScroll, ...rest }, ref) {
  const backdrop = useRef<HTMLDivElement>(null);
  const sync = (e: UIEvent<HTMLTextAreaElement>) => {
    if (backdrop.current) {
      backdrop.current.scrollTop = e.currentTarget.scrollTop;
      backdrop.current.scrollLeft = e.currentTarget.scrollLeft;
    }
    onScroll?.(e);
  };
  return (
    <div className="variables-editor">
      <div ref={backdrop} className="textarea variables-editor__backdrop" aria-hidden="true">
        {splitVariables(value).map((s, i) =>
          s.variable ? (
            <mark key={i} className="variables-editor__token">
              {s.text}
            </mark>
          ) : (
            <span key={i}>{s.text}</span>
          ),
        )}
        {"\n"}
      </div>
      <Textarea
        ref={ref}
        className={["variables-editor__field", className].filter(Boolean).join(" ")}
        value={value}
        onScroll={sync}
        {...rest}
      />
    </div>
  );
});
